/* -- coding: utf-8 -- */
import NotificationStatusChip, { notificationLabel } from '../messages/NotificationStatusChip';
/* eslint-disable unicode-bom */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import trLocale from 'date-fns/locale/tr';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import WhatsApp from '@mui/icons-material/WhatsApp';
import CircularProgress from '@mui/material/CircularProgress';
import { apiUrl, fetchJson } from '../../apiBase';
import RichTextEditor from '../../components/RichTextEditor';
import CreateHomeworkDialog from './CreateHomeworkDialog';
import { isRichTextEmpty, normalizeRichTextSource, normalizeWhatsappText } from '../../utils/richText';

// format helpers

function toDateObj(d) {
  if (!d) return null;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(d)) {
    const [dd, mm, yyyy] = d.split('.').map(Number);
    return new Date(yyyy, mm - 1, dd);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [yyyy, mm, dd] = d.split('-').map(Number);
    return new Date(yyyy, mm - 1, dd);
  }
  return null;
}

function toHumanTRFull(d) {
  const dt = toDateObj(d);
  if (!dt || isNaN(dt.getTime())) return d || '';
  try { return dt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch (_) { return d || ''; }
}

function toHumanTRNoYear(d) {
  const dt = toDateObj(d);
  if (!dt || isNaN(dt.getTime())) return d || '';
  try { return dt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' }); } catch (_) { return d || ''; }
}

function formatDateInline(start, due) {
  const s = toDateObj(start);
  const e = toDateObj(due);
  if (!s) return '';
  if (!e) return toHumanTRNoYear(start);
  if (s.getFullYear() !== e.getFullYear()) {
    return `${toHumanTRFull(start)} – ${toHumanTRFull(due)}`;
  }
  return `${toHumanTRNoYear(start)} – ${toHumanTRNoYear(due)}`;
}

export function getDefaultHomeworkDueDate(assignedDate = new Date()) {
  const dueDate = new Date(assignedDate);
  // JavaScript'te Cuma günü 5'tir; hafta sonunu atlayarak Pazartesiye varsayılan verilir.
  do { dueDate.setDate(dueDate.getDate() + 1); } while (dueDate.getDay() === 0 || dueDate.getDay() === 6);
  return dueDate;
}

export function getPreferredHomeworkLesson(user, scheduleLesson = '') {
  const branch = String(user?.branch || (Array.isArray(user?.subjects) ? user.subjects[0] : user?.subjects) || '').trim();
  const normalized = branch.toLocaleLowerCase('tr-TR');
  if (user?.teacher_role === 'class_teacher' || ['sınıf', 'sınıf öğretmeni', 'sınıf öğretmenliği'].includes(normalized)) return 'Karma Ödev';
  return branch || String(scheduleLesson || '').trim();
}

export default function HomeworksManager({ schoolId, onError, onSuccess, user, createRequest }) {
  const [items, setItems] = useState([]);
  const [archiveItems, setArchiveItems] = useState([]);
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [filterGrade, setFilterGrade] = useState('');

  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialClass, setCreateInitialClass] = useState('');
  const [editing, setEditing] = useState(null);

  const [date, setDate] = useState(null); // Date|null
  const [modalGrade, setModalGrade] = useState('');
  const [className, setClassName] = useState(''); // optional for admins
  const [lesson, setLesson] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(null); // Date|null
  const [errors, setErrors] = useState({}); // field -> message
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeaders = useMemo(() => ({}), []);
  const isTeacher = !!(user && user.role === 'teacher');
  const [teacherId, setTeacherId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const [sendingIds, setSendingIds] = useState(() => new Set()); // ids currently in-flight
  const [sentIds, setSentIds] = useState(() => new Set()); // ids successfully triggered

  const myTeacherId = isTeacher ? String(user?.teacher_id || user?.id || '') : '';

  const loadMeta = useCallback(async () => {
    try {
      const resC = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`), { headers: { ...authHeaders } });
      const resL = await fetchJson(apiUrl(`/admin/lessons/${encodeURIComponent(schoolId)}`), { headers: { ...authHeaders } });
      if (resC.ok) setClasses(await resC.json());
      if (resL.ok) setLessons(await resL.json());

      // Teacher list is only needed when an admin/staff user must choose a teacher.
      if (!isTeacher) {
        const resT = await fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`), { headers: { ...authHeaders } });
        if (resT.ok) setTeachers(await resT.json());
      } else {
        setTeachers([]);
      }
    } catch (e) { /* ignore */ }
  }, [schoolId, authHeaders, isTeacher]);

  // Build grade + class options
  const classOptions = useMemo(() => {
    return (classes || [])
      .map(c => (typeof c === 'string' ? { id: '', name: c } : { id: String(c?.id || ''), name: c?.name || c?.code || '', grade: String(c?.grade_level || '') }))
      .filter(c => c.name);
  }, [classes]);
  const resolveClassIdByName = useCallback((name) => {
    const target = String(name || '').trim();
    if (!target) return '';
    const hit = (classOptions || []).find((c) => String(c?.name || '').trim() === target);
    return String(hit?.id || '');
  }, [classOptions]);
  const classNames = useMemo(() => classOptions.map(c => c.name), [classOptions]);
  const gradeOptions = useMemo(() => [...new Set(classOptions.map((item) => item.grade).filter(Boolean))]
    .sort((a, b) => Number(a) - Number(b) || a.localeCompare(b, 'tr')), [classOptions]);

  const classesForModal = useMemo(() => {
    if (!modalGrade) return [];
    const byGrade = classOptions.filter(item => item.grade === modalGrade).map(item => item.name);
    return byGrade;
  }, [classOptions, modalGrade]);
  const singleTeacherClass = isTeacher && classNames.length === 1 ? classNames[0] : '';
  const gradeForClass = (name) => classOptions.find((item) => item.name === name)?.grade || '';

  const fmtSave = (d) => {
    if (!d || isNaN(new Date(d).getTime())) return '';
    const t = new Date(d);
    const dd = String(t.getDate()).padStart(2, '0');
    const mm = String(t.getMonth() + 1).padStart(2, '0');
    const yyyy = t.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  // Load items for selected grade
  const fetchGroupedForClasses = useCallback(async (limited, bucket) => {
    if (!limited || !limited.length) return [];
    const results = await Promise.all(limited.map(async (cls) => {
      try {
        const q = new URLSearchParams();
        if (cls.id) q.set('class_id', cls.id);
        if (bucket) q.set('bucket', bucket);
        const r = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}?${q}`), { headers: { ...authHeaders } });
        if (!r.ok) return [];
        const body = await r.json();
        return Array.isArray(body) ? body : [];
      } catch (_) { return []; }
    }));

    return [...new Map(results.flat().filter(Boolean).map((item) => [String(item.id), item])).values()].map((h) => ({
      ...h,
      isGroup: false,
      label: h.class_codes?.length ? h.class_codes.join(', ') : String(h.class || ''),
      description: normalizeWhatsappText(h.description || ''),
      descriptionHtml: normalizeRichTextSource(h.description || ''),
    }));
  }, [schoolId, authHeaders]);

  const load = useCallback(async () => {
    if (!filterGrade) { setItems([]); return; }
    setLoading(true);
    try {
      const opts = (classes || [])
        .map(c => (typeof c === 'string' ? { id: '', name: c } : { id: String(c?.id || ''), name: c?.name || c?.code || '', grade: String(c?.grade_level || '') }))
        .filter(c => c.name);
      const byGrade = opts.filter(c => c.grade === filterGrade);
      // /admin/classes already returns the teacher’s assigned classes.
      const limited = byGrade;
      if (!limited.length) { setItems([]); return; }
      const grouped = await fetchGroupedForClasses(limited, 'active');
      setItems(grouped);

      // Reset archive state when grade changes / reloads.
      setArchiveItems([]);
      setArchiveLoaded(false);
    } catch (e) { onError && onError('Ödevler yüklenemedi'); }
    finally { setLoading(false); }
  }, [filterGrade, classes, fetchGroupedForClasses, onError]);

  const loadArchive = useCallback(async () => {
    if (!filterGrade) return;
    const opts = (classes || [])
      .map(c => (typeof c === 'string' ? { id: '', name: c } : { id: String(c?.id || ''), name: c?.name || c?.code || '', grade: String(c?.grade_level || '') }))
      .filter(c => c.name);
    const byGrade = opts.filter(c => c.grade === filterGrade);
    const limited = byGrade;
    if (!limited.length) { setArchiveItems([]); setArchiveLoaded(true); return; }
    setArchiveLoading(true);
    try {
      const grouped = await fetchGroupedForClasses(limited, 'archived');
      setArchiveItems(grouped);
      setArchiveLoaded(true);
    } catch (_) {
      onError && onError('Arşiv ödevler yüklenemedi');
    } finally {
      setArchiveLoading(false);
    }
  }, [filterGrade, classes, fetchGroupedForClasses, onError]);

  useEffect(() => { if (schoolId) { loadMeta(); } }, [schoolId, loadMeta]);
  useEffect(() => { try { const v = localStorage.getItem('homeworks_filter_grade'); if (v) setFilterGrade(v); } catch (_) {} }, []);
  useEffect(() => { try { localStorage.setItem('homeworks_filter_grade', filterGrade || ''); } catch (_) {} }, [filterGrade]);
  const singleTeacherGrade = gradeForClass(singleTeacherClass);
  useEffect(() => {
    if (singleTeacherGrade) setFilterGrade(singleTeacherGrade);
  }, [singleTeacherGrade]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setCreateInitialClass(''); setCreateOpen(true); };
  useEffect(() => { setCreateOpen(false); setOpen(false); }, [schoolId]);
  useEffect(() => {
    if (createRequest?.schoolId === schoolId && createRequest?.id) {
      setCreateInitialClass(String(createRequest.classId || ''));
      setCreateOpen(true);
    }
  }, [createRequest, schoolId]);

  const openEdit = (row) => {
    setEditing(row);
    setDate(toDateObj(row.date) || null);
    if (row.isGroup) {
      setModalGrade(filterGrade || '');
      setClassName('');
    } else {
      const cls = String(row.class || '');
      setModalGrade(gradeForClass(cls) || filterGrade || '');
      setClassName(row.class || '');
    }
    setLesson(row.lesson || '');
    setDescription(normalizeRichTextSource(row.description || ''));
    setDueDate(toDateObj(row.due_date) || null);
    setErrors({});
    setFormError('');
    setOpen(true);
  };

  const resetModal = () => { setOpen(false); setEditing(null); };

  const lessonsOptions = (lessons || [])
    .map(l => (typeof l === 'string' ? { name: l } : l))
    .filter((item) => {
      if (['SINIF', 'TOPLAM'].includes(item?.code)) return false;
      const grades = item?.grade_levels || [];
      return !/^\d+$/.test(String(modalGrade)) || !grades.length || grades.map(String).includes(String(modalGrade))
        || (editing && item?.name === editing.lesson);
    });
  if (editing?.lesson && !lessonsOptions.some((item) => item.name === editing.lesson)) {
    lessonsOptions.push({ id: editing.lesson_id, name: editing.lesson });
  }
  const teacherOptions = (Array.isArray(teachers) ? teachers : (teachers?.teachers || [])).map((t) => ({ id: String(t?.id || ''), label: `${t?.name || ''} ${t?.surname || ''}`.trim() || String(t?.id || '') })).filter(x => x.id && x.label);

  const save = async (sendAfterSave = false) => {
    if (saving) return;
    try {
      const newErrors = {};
      if (!date) newErrors.date = 'Gerekli';
      if (!modalGrade) newErrors.modalGrade = 'Gerekli';
      if (!lesson || !lessonsOptions.some((item) => item.name === lesson)) newErrors.lesson = 'Sınıfa uygun bir ders seçin';
      if (isRichTextEmpty(description)) newErrors.description = 'Gerekli';
      if (!isTeacher && !teacherId) newErrors.teacherId = 'Öğretmen seçin';
      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
        setFormError('Lütfen zorunlu alanları doldurun');
        return;
      }
      setErrors({});
      setFormError('');
      setSaving(true);

      if (editing && editing.isGroup) {
        // Update all grouped items with same fields; do NOT override class_name
        const base = {
          date: fmtSave(date),
          lesson,
          description: normalizeWhatsappText(description),
          due_date: fmtSave(dueDate) || '',
          ...(isTeacher ? { teacher_id: myTeacherId } : { teacher_id: teacherId })
        };
        const ids = Array.isArray(editing.groupIds) ? editing.groupIds : [];
        const results = await Promise.all(ids.map(async (id) => {
          try {
            const body = { ...base };
            const r = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}/${encodeURIComponent(id)}`), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(body) });
            return r.ok;
          } catch (_) { return false; }
        }));
        const okc = results.filter(Boolean).length;
        if (!okc) throw new Error('save-group');
        onSuccess && onSuccess(`Ödev güncellendi (${okc}/${ids.length})`);
        resetModal();
        load();
        return;
      }

      // Single create/update
      const resolvedClassId = resolveClassIdByName(className);
      const body = {
        date: fmtSave(date),
        // Backend expects class_id for POST. Keep class_name for PUT (class change).
        class_id: resolvedClassId,
        class_name: editing?.class_ids?.length > 1 ? undefined : className,
        lesson,
        description: normalizeWhatsappText(description),
        due_date: fmtSave(dueDate) || '',
        ...(isTeacher ? { teacher_id: myTeacherId } : { teacher_id: teacherId })
      };

      if (!resolvedClassId) {
        setErrors((prev) => ({ ...prev, className: 'Şube seçin' }));
        setFormError('Şube bilgisi bulunamadı. Lütfen Şube alanından seçim yapın.');
        return;
      }
      let res;
      if (editing) {
        res = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}/${encodeURIComponent(editing.id)}`), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(body) });
      } else {
        res = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(body) });
      }
      if (!res.ok) throw new Error('save');
      const created = !editing && sendAfterSave ? await res.json() : null;
      onSuccess && onSuccess('Ödev kaydedildi');
      resetModal();
      load();
      if (created?.id) await sendWhatsApp(created);
    } catch (e) { onError && onError('Ödev kaydedilemedi'); setFormError('İşlem sırasında hata oluştu'); }
    finally { setSaving(false); }
  };

  const openRemoveConfirm = (row) => { setConfirmTarget(row); setConfirmOpen(true); };
  const removeConfirmed = async () => {
    const row = confirmTarget; if (!row) { setConfirmOpen(false); return; }
    try {
      if (row.isGroup && Array.isArray(row.groupIds)) {
        const results = await Promise.all(row.groupIds.map(async (id) => {
          try {
            const r = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}/${encodeURIComponent(id)}`), { method: 'DELETE', headers: authHeaders });
            return r.ok;
          } catch (_) { return false; }
        }));
        const okc = results.filter(Boolean).length;
        if (!okc) throw new Error('del-group');
        onSuccess && onSuccess(`Ödev(ler) silindi (${okc}/${row.groupIds.length})`);
        load();
      } else {
        const res = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}/${encodeURIComponent(row.id)}`), { method: 'DELETE', headers: authHeaders });
        if (!res.ok) throw new Error('del');
        onSuccess && onSuccess('Ödev silindi');
        load();
      }
    } catch (e) { onError && onError('Ödev silinemedi'); }
    finally { setConfirmOpen(false); setConfirmTarget(null); }
  };

  const isItemFullySent = (row) => {
    if (!row) return false;
    if (row.notification_status) return true; // Prevent duplicate sharing, not proof of delivery.
    if (row.isGroup && Array.isArray(row.groupIds)) {
      return row.groupIds.every(id => sentIds.has(String(id)));
    }
    return sentIds.has(String(row.id));
  };
  const itemDeliveryLabel = (row) => notificationLabel(row.notification_status || (isItemFullySent(row) ? 'pending' : null), row.notification_counts);

  const sendWhatsApp = async (row) => {
    if (!row) return;
    const targetIds = row.isGroup && Array.isArray(row.groupIds) ? row.groupIds.slice() : [row.id];
    const pending = new Set(sendingIds);
    targetIds.forEach(id => pending.add(String(id)));
    setSendingIds(pending);
    let okCount = 0;
    try {
      for (const id of targetIds) {
        try {
          const res = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}/${encodeURIComponent(id)}/send-messages`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
          });
          if (res.ok) {
            okCount += 1;
            setSentIds(prev => new Set(prev).add(String(id)));
          }
        } catch (_) { /* ignore single id failure */ }
      }
      if (okCount > 0) {
        onSuccess && onSuccess(`WhatsApp bildirimleri kuyruğa eklendi (${okCount}/${targetIds.length} ödev)`);
        load();
      } else {
        onError && onError('WhatsApp gönderimi başarısız');
      }
    } finally {
      const done = new Set(sendingIds);
      targetIds.forEach(id => done.delete(String(id)));
      setSendingIds(done);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <Autocomplete
              size="small"
              disabled={!!singleTeacherClass}
              options={gradeOptions}
              value={filterGrade || null}
              onChange={(_, v) => setFilterGrade(v || '')}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} label="Sınıf Seviyesi" placeholder="Örn: 5, 6, 7, 8, Mezun" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={9} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Tooltip title="Yeni Ödev">
              <span>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={openNew}>
                  Yeni Ödev
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Ödevler</Typography>
        {!filterGrade ? (
          <Box sx={{ py: 3, color: 'text.secondary' }}>
            <Typography variant="body1">Önce sınıf seviyesi seçin</Typography>
          </Box>
        ) : loading ? (
          <List>
            {Array.from({ length: 6 }).map((_, i) => (
              <ListItem key={`s-${i}`} divider>
                <ListItemText primary={<Skeleton variant="text" width="60%" />} secondary={<Skeleton variant="text" width="40%" />} />
              </ListItem>
            ))}
          </List>
        ) : items.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>Henüz ödev eklenmedi</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>+ Yeni Ödev</Button>
          </Box>
        ) : (
          <List>
            {items
              .slice()
              .sort((a, b) => {
                const ad = toDateObj(a.date)?.getTime() || 0;
                const bd = toDateObj(b.date)?.getTime() || 0;
                return bd - ad;
              })
              .map((it) => (
                <ListItem
                  key={it.id}
                  divider
                  onClick={() => openEdit(it)}
                  sx={{ borderRadius: 1, '&:hover': { bgcolor: '#F9FAFB', cursor: 'pointer' }, transition: 'background-color 0.2s ease' }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Tooltip title="Düzenle">
                        <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openEdit(it); }} sx={{ width: 40, height: 40, color: 'text.secondary', '&:hover': { bgcolor: '#F5F5F5', color: 'text.primary' } }} aria-label="Düzenle">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openRemoveConfirm(it); }} sx={{ width: 40, height: 40, color: 'text.secondary', '&:hover': { bgcolor: '#F5F5F5', color: 'text.primary' } }} aria-label="Sil">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isItemFullySent(it) ? itemDeliveryLabel(it) : 'WhatsApp mesajlarını gönder'}>
                        <span>
                          <IconButton
                            edge="end"
                            onClick={(e) => { e.stopPropagation(); sendWhatsApp(it); }}
                            sx={{
                              minWidth: 40,
                              height: 40,
                              color: isItemFullySent(it) ? 'success.main' : 'text.secondary',
                              '&:hover': { bgcolor: '#F5F5F5', color: isItemFullySent(it) ? 'success.dark' : 'text.primary' }
                            }}
                            aria-label={isItemFullySent(it) ? `WhatsApp: ${itemDeliveryLabel(it)}` : 'WhatsApp mesajlarını gönder'}
                            disabled={isItemFullySent(it) || (sendingIds.size > 0 && (it.isGroup ? it.groupIds.some(id => sendingIds.has(String(id))) : sendingIds.has(String(it.id))))}
                          >
                            {isItemFullySent(it) ? (
                              <NotificationStatusChip status={it.notification_status || 'pending'} counts={it.notification_counts} />
                            ) : (sendingIds.size > 0 && (it.isGroup ? it.groupIds.some(id => sendingIds.has(String(id))) : sendingIds.has(String(it.id)))) ? (
                              <CircularProgress size={20} thickness={5} />
                            ) : (
                              <WhatsApp fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={(
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>{it.lesson || ''}</Box>
                        <Box component="span" sx={{ mx: 0.6, color: 'text.disabled' }}>•</Box>
                        <Box
                          component="span"
                          sx={{
                            whiteSpace: 'nowrap',
                            fontSize: '0.85rem',
                            lineHeight: 1.2,
                            color: 'text.secondary'
                          }}
                        >
                          {formatDateInline(it.date, it.due_date)}
                        </Box>
                      </Box>
                    )}
                    secondary={(
                      <Box sx={{ mt: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: it.descriptionHtml ? 0.5 : 0 }}>
                          <Chip size="small" variant="outlined" label={`Şube: ${it.label || it.class || ''}`} />
                        </Box>
                        {!isRichTextEmpty(it.descriptionHtml || '') && (
                          <Typography
                            component="div"
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.55,
                              mt: 0.5,
                              maxHeight: 96,
                              overflow: 'hidden',
                              position: 'relative',
                              pr: 1,
                              '& ul, & ol': { pl: 2.5, mb: 1 },
                              '& li': { mb: 0.5 },
                              '& p': { m: 0, mb: 1 },
                              '& p:last-of-type': { mb: 0 },
                            }}
                            dangerouslySetInnerHTML={{ __html: (it.descriptionHtml || normalizeRichTextSource(it.description || '')).replace(/\s+$/, '') }}
                          />
                        )}
                      </Box>
                    )}
                  />
                </ListItem>
              ))}
          </List>
        )}
      </Paper>

      {filterGrade ? (
        <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Arşiv</Typography>
            {archiveLoaded ? (
              <Button size="small" variant="outlined" onClick={() => { setArchiveLoaded(false); setArchiveItems([]); }}>
                Arşivi Gizle
              </Button>
            ) : (
              <Button size="small" variant="outlined" onClick={loadArchive} disabled={archiveLoading}>
                Arşiv Ödevleri Yükle
              </Button>
            )}
          </Box>

          {!archiveLoaded ? (
            <Box sx={{ py: 1, color: 'text.secondary' }}>
              <Typography variant="body2">Geçmiş ödevleri görmek için yükleyin.</Typography>
            </Box>
          ) : archiveLoading ? (
            <List>
              {Array.from({ length: 4 }).map((_, i) => (
                <ListItem key={`as-${i}`} divider>
                  <ListItemText primary={<Skeleton variant="text" width="60%" />} secondary={<Skeleton variant="text" width="40%" />} />
                </ListItem>
              ))}
            </List>
          ) : archiveItems.length === 0 ? (
            <Box sx={{ py: 2, color: 'text.secondary' }}>
              <Typography variant="body2">Arşivde ödev bulunamadı.</Typography>
            </Box>
          ) : (
            <List>
              {archiveItems
                .slice()
                .sort((a, b) => {
                  const ad = toDateObj(a.date)?.getTime() || 0;
                  const bd = toDateObj(b.date)?.getTime() || 0;
                  return bd - ad;
                })
                .map((it) => (
                  <ListItem
                    key={`arch-${it.id}`}
                    divider
                    onClick={() => openEdit(it)}
                    sx={{ borderRadius: 1, '&:hover': { bgcolor: '#F9FAFB', cursor: 'pointer' }, transition: 'background-color 0.2s ease' }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Tooltip title="Düzenle">
                          <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openEdit(it); }} sx={{ width: 40, height: 40, color: 'text.secondary', '&:hover': { bgcolor: '#F5F5F5', color: 'text.primary' } }} aria-label="Düzenle">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openRemoveConfirm(it); }} sx={{ width: 40, height: 40, color: 'text.secondary', '&:hover': { bgcolor: '#F5F5F5', color: 'text.primary' } }} aria-label="Sil">
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={(
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>{it.lesson || ''}</Box>
                          <Box component="span" sx={{ mx: 0.6, color: 'text.disabled' }}>•</Box>
                          <Box
                            component="span"
                            sx={{
                              whiteSpace: 'nowrap',
                              fontSize: '0.85rem',
                              lineHeight: 1.2,
                              color: 'text.secondary'
                            }}
                          >
                            {formatDateInline(it.date, it.due_date)}
                          </Box>
                        </Box>
                      )}
                      secondary={(
                        <Box sx={{ mt: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: it.descriptionHtml ? 0.5 : 0 }}>
                            <Chip size="small" variant="outlined" label={`Şube: ${it.label || it.class || ''}`} />
                          </Box>
                          {!isRichTextEmpty(it.descriptionHtml || '') && (
                            <Typography
                              component="div"
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                lineHeight: 1.55,
                                mt: 0.5,
                                maxHeight: 96,
                                overflow: 'hidden',
                                position: 'relative',
                                pr: 1,
                                '& ul, & ol': { pl: 2.5, mb: 1 },
                                '& li': { mb: 0.5 },
                                '& p': { m: 0, mb: 1 },
                                '& p:last-of-type': { mb: 0 },
                              }}
                              dangerouslySetInnerHTML={{ __html: (it.descriptionHtml || normalizeRichTextSource(it.description || '')).replace(/\s+$/, '') }}
                            />
                          )}
                        </Box>
                      )}
                    />
                  </ListItem>
                ))}
            </List>
          )}
        </Paper>
      ) : null}

      {createOpen && <CreateHomeworkDialog key={String(schoolId)} schoolId={schoolId} user={user}
        initialClassId={createInitialClass}
        onClose={() => setCreateOpen(false)} onError={onError} onCreated={(created) => {
          setCreateOpen(false);
          onSuccess?.(`Ödev ${(created.class_codes || []).join(' ve ')} şubelerine gönderildi.`);
          if (created.grade_levels?.[0]) setFilterGrade(String(created.grade_levels[0]));
          load();
        }} />}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ödev</DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {formError}
            </Alert>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker label="Tarih" value={date} onChange={(v) => { setDate(v); if (errors.date) setErrors(prev => ({ ...prev, date: undefined })); }} format="dd.MM.yyyy" slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.date, helperText: errors.date } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete size="small" disabled={editing?.class_ids?.length > 1} options={gradeOptions} value={modalGrade || null} onChange={(_, v) => { setModalGrade(v || ''); setClassName(''); setLesson(''); if (errors.modalGrade) setErrors(prev => ({ ...prev, modalGrade: undefined })); }} renderInput={(params) => (<TextField {...params} label="Sınıf Seviyesi" placeholder="Örn: 5, 6, 7, 8, Mezun" fullWidth error={!!errors.modalGrade} helperText={errors.modalGrade} />)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete size="small" disabled={editing?.class_ids?.length > 1} options={classesForModal} value={className || null} onChange={(_, v) => setClassName(v || '')} renderInput={(params) => (<TextField {...params} label="Şube" fullWidth />)} />
                {editing?.class_ids?.length > 1 && <Typography variant="caption">Şubeler: {(editing.class_codes || []).join(', ')}</Typography>}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Ders" value={lesson} onChange={(e) => { setLesson(e.target.value); if (errors.lesson) setErrors(prev => ({ ...prev, lesson: undefined })); }} size="small" fullWidth error={!!errors.lesson} helperText={errors.lesson}>
                  {lessonsOptions.map((l) => (<MenuItem key={l.name} value={l.name}>{l.name}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker label="Son Tarih (opsiyonel)" value={dueDate} onChange={(v) => setDueDate(v)} format="dd.MM.yyyy" slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              </Grid>
              {!isTeacher && (
                <Grid item xs={12}>
                  <TextField select label="Öğretmen" value={teacherId} onChange={(e) => { setTeacherId(e.target.value); if (errors.teacherId) setErrors(prev => ({ ...prev, teacherId: undefined })); }} size="small" fullWidth error={!!errors.teacherId} helperText={errors.teacherId}>
                    {teacherOptions.map((t) => (<MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12}>
                <RichTextEditor value={description} onChange={(val) => { setDescription(val); if (errors.description) setErrors(prev => ({ ...prev, description: undefined })); }} placeholder="Açıklama" />
                {errors.description && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.description}</Typography>
                )}
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={() => save()} disabled={saving}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Ödev silinsin mi?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>İptal</Button>
          <Button color="error" variant="contained" onClick={removeConfirmed}>Sil</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
